import React from 'react';
import { usernamePolicy } from '../lib/identity-policy.js';

const DEFAULT_PREFERENCES = {
  last_seen: {mode: 'everyone'},
  online: {mode: 'everyone'},
  profile_photo: {mode: 'everyone'},
  about: {mode: 'everyone'},
  block_unknown_messages: false,
  media_upload_quality: 'standard',
  media_auto_download: 'wifi',
  notifications: {
    messages: true, calls: true, groups: true, channels: true, updates: true, video_calls: true
  },
  call_rejection_messages: ['I’ll call you back later.', 'Call back in 10 minutes.']
};

const PRIVACY_FIELDS = [
  ['last_seen', 'Last seen', false],
  ['online', 'Online status', true],
  ['profile_photo', 'Profile photo', false],
  ['about', 'About', false]
];

const NOTIFICATION_FIELDS = [
  ['messages', 'Messages'], ['calls', 'Audio calls'], ['video_calls', 'Video calls'],
  ['groups', 'Groups'], ['channels', 'Channels'], ['updates', 'Updates']
];

function requestId() {
  return (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() :
    Date.now().toString(36) + Math.random().toString(36).slice(2);
}

function copyPreferences(value) {
  const p = Object.assign({}, DEFAULT_PREFERENCES, value || {});
  p.notifications = Object.assign({}, DEFAULT_PREFERENCES.notifications, p.notifications || {});
  p.call_rejection_messages = Array.isArray(p.call_rejection_messages) ? [...p.call_rejection_messages] : [...DEFAULT_PREFERENCES.call_rejection_messages];
  PRIVACY_FIELDS.forEach(([key]) => {
    p[key] = Object.assign({mode: 'everyone', selected: []}, p[key] || {});
  });
  return p;
}

export default class ProfileSettingsView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      saving: '',
      name: '', about: '', username: '', usernameDraft: '', usernamePrice: 20, balance: 0,
      preferences: copyPreferences(), exportRequest: null, delivery: 'download', deleteText: '',
      recordingPolicy: null,
      cloudBackup: {google_drive:false,onedrive:false,google_connected:false,onedrive_connected:false},
	  locationProvider: 'ask', usernameAvailable: true, usernameStatus: ''
    };
  }

  componentWillUnmount() {
	clearTimeout(this.usernameCheckTimer);
  }

  updateUsernameDraft = value => {
	const policy = usernamePolicy(value);
	clearTimeout(this.usernameCheckTimer);
	if (policy.username === this.state.username) {
	  this.setState({usernameDraft:value,usernameAvailable:true,usernameStatus:'This is your current username'});
	  return;
	}
	if (!policy.valid) {
	  this.setState({usernameDraft:value,usernameAvailable:false,usernameStatus:'Use 6–15 letters and numbers only'});
	  return;
	}
	this.setState({usernameDraft:value,usernameAvailable:false,usernameStatus:'Checking availability…'});
	this.usernameCheckTimer=setTimeout(()=>this.props.kaltiq.usernameAvailability(policy.username)
	  .then(result=>{if(usernamePolicy(this.state.usernameDraft).username===policy.username)this.setState({usernameAvailable:!!result.available,usernameStatus:result.message});})
	  .catch(()=>this.setState({usernameAvailable:false,usernameStatus:'Availability could not be checked'})),300);
  };

  componentDidMount() {
    Promise.all([
      this.props.kaltiq.profile(),
      this.props.kaltiq.preferences(),
      this.props.kaltiq.wallet(),
      this.props.kaltiq.recordingPolicy(),
      this.props.kaltiq.cloudBackupPreference(),
      this.props.kaltiq.locationPreference(),
      this.props.kaltiq.exportStatus().catch(err => err.status === 404 ? null : Promise.reject(err))
    ]).then(([profile, preferences, wallet, recordingPolicy, cloudBackup, locationPreference, exportRequest]) => {
      this.setState({
        loading: false,
        name: profile.name || '', about: profile.about || '', username: profile.username || '',
        usernameDraft: profile.username || '', preferences: copyPreferences(preferences),
        usernamePrice: wallet.pricing && wallet.pricing.username_price === 0 ? 0 :
          ((wallet.pricing && wallet.pricing.username_price) || 20),
        balance: wallet.balance || 0, recordingPolicy, cloudBackup, locationProvider: locationPreference.provider || 'ask', exportRequest
      });
    }).catch(err => {
      this.setState({loading: false});
      this.props.onError(err.message, 'err');
    });
  }

  notify(message) {
    this.props.onError(message, 'info');
  }

  saveProfile = () => {
    this.setState({saving: 'profile'});
    this.props.kaltiq.updateProfile(this.state.name, this.state.about)
      .then(profile => {
        this.setState({saving: ''});
        this.notify('Profile saved');
        const me = this.props.kaltiq && this.props.kaltiq.getMeTopic();
        if (me) {
          me.public = Object.assign({}, me.public || {}, {fn: profile.name, note: profile.about});
        }
      })
      .catch(err => { this.setState({saving: ''}); this.props.onError(err.message, 'err'); });
  };

  changeUsername = () => {
	const username = usernamePolicy(this.state.usernameDraft).username;
    if (username === this.state.username) {
      return;
    }
	if (!this.state.usernameAvailable || !usernamePolicy(username).valid) {
	  this.props.onError('Choose an available username using 6–15 letters and numbers only.', 'err');
	  return;
	}
    if (!window.confirm(`Change your username to @${username}? This costs ${this.state.usernamePrice} Kal coins. The old name will be unavailable for 30 days.`)) {
      return;
    }
    this.setState({saving: 'username'});
    this.props.kaltiq.changeUsername(username, requestId())
      .then(result => {
        this.setState({saving: '', username, usernameDraft: username, balance: result.balance});
        this.notify('Username changed');
      })
      .catch(err => { this.setState({saving: ''}); this.props.onError(err.message, 'err'); });
  };

  setPreference = (key, value) => {
    this.setState(prev => ({preferences: Object.assign({}, prev.preferences, {[key]: value})}));
  };

  setVisibility = (key, mode) => {
    const current = this.state.preferences[key] || {};
    this.setPreference(key, Object.assign({}, current, {mode}));
  };

  setSelected = (key, text) => {
    const selected = text.split(/[\s,]+/).map(item => item.trim()).filter(Boolean);
    this.setPreference(key, Object.assign({}, this.state.preferences[key], {selected}));
  };

  setNotification = (key, checked) => {
    const notifications = Object.assign({}, this.state.preferences.notifications, {[key]: checked});
    this.setPreference('notifications', notifications);
  };

  setRejectionMessages = text => {
    const messages = text.split('\n').map(value => value.trim()).filter(Boolean).slice(0, 10);
    this.setPreference('call_rejection_messages', messages);
  };

  savePreferences = () => {
    this.setState({saving: 'preferences'});
    this.props.kaltiq.savePreferences(this.state.preferences)
      .then(preferences => {
        this.setState({saving: '', preferences: copyPreferences(preferences)});
        this.notify('Preferences saved');
      })
      .catch(err => { this.setState({saving: ''}); this.props.onError(err.message, 'err'); });
  };

  setRecordingEnabled = enabled => {
    this.setState({saving: 'recording'});
    this.props.kaltiq.setRecordingEnabled(enabled)
      .then(recordingPolicy => this.setState({saving: '', recordingPolicy}))
      .catch(err => { this.setState({saving: ''}); this.props.onError(err.message, 'err'); });
  };

  saveConnectedServices = () => {
    this.setState({saving: 'connected-services'});
    Promise.all([
      this.props.kaltiq.saveCloudBackupPreference(this.state.cloudBackup.google_drive, this.state.cloudBackup.onedrive),
      this.props.kaltiq.saveLocationPreference(this.state.locationProvider)
    ]).then(([cloudBackup, locationPreference]) => {
      this.setState({saving:'',cloudBackup,locationProvider:locationPreference.provider});
      this.notify('Backup and location preferences saved');
    }).catch(err => { this.setState({saving:''}); this.props.onError(err.message,'err'); });
  };

  connectCloudBackup = provider => {
    this.setState({saving: `connect-${provider}`});
    this.props.kaltiq.connectCloudBackup(provider)
      .then(result => { if (!result.authorization_url) throw new Error('Provider authorization URL was not returned'); window.location.assign(result.authorization_url); })
      .catch(err => { this.setState({saving:''}); this.props.onError(err.message,'err'); });
  };

  runCloudBackup = provider => {
    this.setState({saving: `backup-${provider}`});
    this.props.kaltiq.runCloudBackup(provider)
      .then(() => { this.setState({saving:''}); this.notify(`${provider === 'google' ? 'Google Drive' : 'OneDrive'} backup completed`); })
      .catch(err => { this.setState({saving:''}); this.props.onError(err.message,'err'); });
  };

  requestExport = () => {
    this.setState({saving: 'export'});
    this.props.kaltiq.requestExport(this.state.delivery)
      .then(exportRequest => {
        this.setState({saving: '', exportRequest});
        this.notify('Account export requested. It will be ready in 24 hours.');
      })
      .catch(err => { this.setState({saving: ''}); this.props.onError(err.message, 'err'); });
  };

  downloadExport = () => {
    this.setState({saving: 'download'});
    this.props.kaltiq.downloadExport().then(data => {
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], {type: 'application/json'}));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'kaltiq-account-export.json';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      this.setState({saving: ''});
    }).catch(err => { this.setState({saving: ''}); this.props.onError(err.message, 'err'); });
  };

  deleteAccount = () => {
    if (this.state.deleteText !== 'DELETE' || !window.confirm('Permanently disable this account and release its username and ICN?')) {
      return;
    }
    this.setState({saving: 'delete'});
    this.props.kaltiq.deleteAccount()
      .then(() => this.props.onLogout())
      .catch(err => { this.setState({saving: ''}); this.props.onError(err.message, 'err'); });
  };

  renderVisibility(key, label, online) {
    const rule = this.state.preferences[key];
    return <div className="kaltiq-setting" key={key}>
      <label htmlFor={`privacy-${key}`}>{label}</label>
      <select id={`privacy-${key}`} value={rule.mode} onChange={e => this.setVisibility(key, e.target.value)}>
        <option value="everyone">Everyone</option>
        {!online ? <option value="contacts">Contacts</option> : null}
        {!online ? <option value="selected">Selected contacts</option> : null}
        <option value="nobody">Nobody</option>
      </select>
      {rule.mode === 'selected' ? <input type="text" value={(rule.selected || []).join(', ')}
        placeholder="Usernames, ICNs, or user IDs" onChange={e => this.setSelected(key, e.target.value)} /> : null}
    </div>;
  }

  render() {
    if (this.state.loading) {
      return <div className="panel-form-row">Loading profile…</div>;
    }
    const ready = this.state.exportRequest && new Date(this.state.exportRequest.ready_at) <= new Date();
    return <div className="scrollable-panel kaltiq-profile-settings">
      <section>
        <h3>Profile</h3>
        <label>Your name <span>{this.state.name.length}/600</span></label>
        <input type="text" maxLength="600" value={this.state.name} onChange={e => this.setState({name: e.target.value})} />
        <label>About <span>{this.state.about.length}/600</span></label>
        <textarea maxLength="600" rows="4" value={this.state.about} onChange={e => this.setState({about: e.target.value})} />
        <button className="primary" disabled={!!this.state.saving} onClick={this.saveProfile}>Save profile</button>
      </section>

      <section>
        <h3>Username</h3>
        <p className="small gray">Changes cost {this.state.usernamePrice} Kal coins. Released names are held for 30 days.</p>
		<input type="text" minLength="6" maxLength="15" pattern="[A-Za-z0-9]{6,15}" value={this.state.usernameDraft}
		  onChange={e => this.updateUsernameDraft(e.target.value)} />
		<div className={'credential-status ' + (this.state.usernameAvailable ? 'met' : 'unmet')} role="status" aria-live="polite">{this.state.usernameStatus || 'Use 6–15 letters and numbers only'}</div>
        <div className="small">Balance: {this.state.balance} Kal coins</div>
		<button disabled={!!this.state.saving || usernamePolicy(this.state.usernameDraft).username === this.state.username || !this.state.usernameAvailable}
          onClick={this.changeUsername}>Change username</button>
      </section>

      <section>
        <h3>Privacy</h3>
        {PRIVACY_FIELDS.map(item => this.renderVisibility(...item))}
        <label className="kaltiq-check"><input type="checkbox" checked={this.state.preferences.block_unknown_messages}
          onChange={e => this.setPreference('block_unknown_messages', e.target.checked)} />
          Block messages from people who are not mutual contacts</label>
        <button onClick={() => this.props.onNavigate('blocked')}>Blocked contacts</button>
      </section>

      <section>
        <h3>Chats and media</h3>
        <div className="kaltiq-setting"><label>Upload quality</label>
          <select value={this.state.preferences.media_upload_quality}
            onChange={e => this.setPreference('media_upload_quality', e.target.value)}>
            <option value="standard">Standard</option><option value="high">High</option>
          </select></div>
        <div className="kaltiq-setting"><label>Automatic downloads</label>
          <select value={this.state.preferences.media_auto_download}
            onChange={e => this.setPreference('media_auto_download', e.target.value)}>
            <option value="never">Never</option><option value="wifi">Wi-Fi only</option><option value="always">Always</option>
          </select></div>
      </section>

      <section>
        <h3>Notifications</h3>
        <div className="kaltiq-check-grid">{NOTIFICATION_FIELDS.map(([key, label]) =>
          <label className="kaltiq-check" key={key}><input type="checkbox"
            checked={this.state.preferences.notifications[key]}
            onChange={e => this.setNotification(key, e.target.checked)} />{label}</label>)}</div>
        <button className="primary" disabled={!!this.state.saving} onClick={this.savePreferences}>Save preferences</button>
      </section>

      <section>
        <h3>Call rejection messages</h3>
        <p className="small gray">One quick reply per line. These appear on the incoming-call screen when you choose Message.</p>
        <textarea rows="5" maxLength="1600" value={(this.state.preferences.call_rejection_messages || []).join('\n')}
          onChange={e => this.setRejectionMessages(e.target.value)} />
        <button className="primary" disabled={!!this.state.saving} onClick={this.savePreferences}>Save quick replies</button>
      </section>

      {this.state.recordingPolicy && this.state.recordingPolicy.granted ? <section>
        <h3>Call recording</h3>
        <p className="small gray">This feature was approved for your account. Recording always requires a visible in-call indicator and participant consent.</p>
        <label className="kaltiq-check"><input type="checkbox" checked={!!this.state.recordingPolicy.user_enabled}
          onChange={e => this.setRecordingEnabled(e.target.checked)} />Enable recording controls on this device</label>
        <button onClick={() => this.props.onNavigate('recordings')}>Open encrypted recordings</button>
      </section> : null}

      <section>
        <h3>Cloud backup and maps</h3>
        <p className="small gray">Choose Google Drive, OneDrive, or both. Backup starts only after you privately connect the selected provider; Kaltiq never asks for your provider password.</p>
        <label className="kaltiq-check"><input type="checkbox" checked={!!this.state.cloudBackup.google_drive}
          onChange={e => this.setState({cloudBackup:Object.assign({},this.state.cloudBackup,{google_drive:e.target.checked})})} />Google Drive {this.state.cloudBackup.google_connected ? '· connected' : '· connection required'}</label>
        <label className="kaltiq-check"><input type="checkbox" checked={!!this.state.cloudBackup.onedrive}
          onChange={e => this.setState({cloudBackup:Object.assign({},this.state.cloudBackup,{onedrive:e.target.checked})})} />OneDrive {this.state.cloudBackup.onedrive_connected ? '· connected' : '· connection required'}</label>
        <div className="row">
          <button disabled={!!this.state.saving || !this.state.cloudBackup.google_drive} onClick={() => this.state.cloudBackup.google_connected ? this.runCloudBackup('google') : this.connectCloudBackup('google')}>{this.state.cloudBackup.google_connected ? 'Back up to Google Drive now' : 'Connect Google Drive'}</button>
          <button disabled={!!this.state.saving || !this.state.cloudBackup.onedrive} onClick={() => this.state.cloudBackup.onedrive_connected ? this.runCloudBackup('onedrive') : this.connectCloudBackup('onedrive')}>{this.state.cloudBackup.onedrive_connected ? 'Back up to OneDrive now' : 'Connect OneDrive'}</button>
        </div>
        <div className="kaltiq-setting"><label>Open shared locations with</label><select value={this.state.locationProvider}
          onChange={e => this.setState({locationProvider:e.target.value})}><option value="ask">Ask every time</option><option value="google">Google Maps</option><option value="here">HERE WeGo</option></select></div>
        <button className="primary" disabled={!!this.state.saving} onClick={this.saveConnectedServices}>Save connected-service preferences</button>
      </section>

      <section>
        <h3>Account information</h3>
        <p className="small gray">For security, exports become available 24 hours after you request them.</p>
        <select value={this.state.delivery} onChange={e => this.setState({delivery: e.target.value})}>
          <option value="download">Direct download</option><option value="email">Email</option>
        </select>
        <button disabled={!!this.state.saving} onClick={this.requestExport}>Request export</button>
        {this.state.exportRequest ? <div className="small">Requested {new Date(this.state.exportRequest.requested_at).toLocaleString()} ·
          ready {new Date(this.state.exportRequest.ready_at).toLocaleString()}
          {ready && this.state.exportRequest.delivery === 'download' ?
            <button onClick={this.downloadExport}>Download JSON</button> : null}</div> : null}
      </section>

      <section className="kaltiq-danger-zone">
        <h3>Delete account</h3>
        <p className="small">Type DELETE to confirm. Your username enters a 30-day hold and your ICN a 90-day cooldown.</p>
        <input type="text" value={this.state.deleteText} onChange={e => this.setState({deleteText: e.target.value})} />
        <button disabled={this.state.deleteText !== 'DELETE' || !!this.state.saving} onClick={this.deleteAccount}>Delete account</button>
      </section>
    </div>;
  }
}
